
import { supabase } from "@/integrations/supabase/client";
import { Guard } from "../types";

/**
 * Fetches all guards in teams that the current user has access to
 */
export const getGuardsInUserTeams = async (): Promise<Guard[]> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    console.log('Fetching guards for user:', user.id);
    
    // First get the teams the user has access to
    const teamIds = await getUserTeamIds(user.id);
    
    if (teamIds.length === 0) {
      console.log('User has no team access');
      return [];
    }

    console.log('User team IDs:', teamIds);

    // Get all guards in those teams - including archived ones for schedule display
    const { data: guards, error } = await supabase
      .from('profiles')
      .select(`
        *,
        team_members!inner(team_id),
        user_roles(role)
      `)
      .in('team_members.team_id', teamIds)
      .order('first_name');

    if (error) {
      console.error('Error fetching guards:', error);
      throw error;
    }

    console.log(`Found ${guards?.length || 0} guards`);
    return (guards || []).map(g => ({
      ...g,
      Role: (g.user_roles?.[0]?.role || g.Role) as Guard['Role']
    })) as Guard[];
  } catch (error) {
    console.error('Error in getGuardsInUserTeams:', error);
    throw error;
  }
};

/**
 * Fetches guards by team ID - includes archived guards for schedule continuity
 */
export const getGuardsByTeamId = async (teamId: string): Promise<Guard[]> => {
  try {
    const { data: guards, error } = await supabase
      .from('profiles')
      .select(`
        *,
        team_members!inner(team_id),
        user_roles(role)
      `)
      .eq('team_members.team_id', teamId)
      .order('first_name');

    if (error) {
      console.error('Error fetching guards by team ID:', error);
      throw error;
    }

    return (guards || []).map(g => ({
      ...g,
      Role: (g.user_roles?.[0]?.role || g.Role) as Guard['Role']
    })) as Guard[];
  } catch (error) {
    console.error('Error in getGuardsByTeamId:', error);
    throw error;
  }
};

/**
 * Get team IDs that the current user has access to
 */
export const getUserTeamIds = async (userId: string): Promise<string[]> => {
  try {
    // Check if user is admin first (includes contributors)
    const { data: adminCheck } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId)
      .in('role', ['admin', 'super_admin']);
      
    if (adminCheck && adminCheck.length > 0) {
      // Admin/contributor can see teams - RLS will filter based on admin group
      const { data: allTeams, error } = await supabase
        .from('teams')
        .select('id');
        
      if (error) throw error;
      return allTeams?.map(team => team.id) || [];
    }

    const teamIds: string[] = [];

    // Get teams where user is a member
    const { data: memberTeams, error: memberError } = await supabase
      .from('team_members')
      .select('team_id')
      .eq('profile_id', userId);

    if (memberError) {
      console.error('Error fetching member teams:', memberError);
    } else {
      teamIds.push(...(memberTeams?.map(tm => tm.team_id) || []));
    }

    // Get teams where user is assigned as client
    const { data: clientTeams, error: clientError } = await supabase
      .from('client_teams')
      .select('team_id')
      .eq('client_id', userId);

    if (clientError) {
      console.error('Error fetching client teams:', clientError);
    } else {
      teamIds.push(...(clientTeams?.map(ct => ct.team_id) || []));
    }

    // Get teams where user is a manager
    const { data: managerTeams, error: managerError } = await supabase
      .from('manager_teams')
      .select('team_id')
      .eq('manager_id', userId);

    if (managerError) {
      console.error('Error fetching manager teams:', managerError);
    } else {
      teamIds.push(...(managerTeams?.map(mt => mt.team_id) || []));
    }

    // Remove duplicates and return
    return [...new Set(teamIds)];
  } catch (error) {
    console.error('Error in getUserTeamIds:', error);
    throw error;
  }
};

/**
 * Fetches archived guards - separate function for management purposes
 */
export const getArchivedGuards = async (): Promise<Guard[]> => {
  try {
    const { data: guards, error } = await supabase
      .from('profiles')
      .select(`
        *,
        user_roles(role)
      `)
      .eq('status', 'archived')
      .order('first_name');

    if (error) {
      console.error('Error fetching archived guards:', error);
      throw error;
    }

    return (guards || []).map(g => ({
      ...g,
      Role: (g.user_roles?.[0]?.role || g.Role) as Guard['Role']
    })) as Guard[];
  } catch (error) {
    console.error('Error in getArchivedGuards:', error);
    throw error;
  }
};
